/**
 * threeMfExporter.ts
 *
 * Converts a Three.js BufferGeometry (or a raw binary STL Blob) into a .3mf
 * file that any slicer can open — Bambu Studio, PrusaSlicer, Cura, Orca, etc.
 *
 * 3MF is a ZIP archive with the following structure:
 *   [Content_Types].xml
 *   _rels/.rels
 *   3D/3dmodel.model   ← the actual geometry + metadata XML
 *
 * The geometry is stored as indexed triangle soup in millimetres inside the
 * XML. All slicer-relevant print settings are stored as <metadata> elements
 * which Bambu Studio / PrusaSlicer will pick up automatically.
 *
 * We build the ZIP from scratch so we don't need any runtime dependency.
 * The approach uses CRC-32, local file headers and a central directory as
 * required by the ZIP specification (APPNOTE.TXT §4.3).
 */

import * as THREE from 'three';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface PrintSettings {
  /** Model name shown in the slicer UI */
  modelName: string;
  /** Unit of the parameter values (always mm for our SCAD pipeline) */
  unit: 'millimeter' | 'inch';
  /** Uniform scale applied before export (1.0 = no change) */
  scale: number;
  /** Place object centre on build plate */
  centreOnPlate: boolean;
  /** Hint to the slicer to auto-generate supports */
  needsSupport: boolean;
  /** Human-readable quality label stored as metadata */
  quality: 'draft' | 'standard' | 'fine';
}

export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  modelName: 'gienox-model',
  unit: 'millimeter',
  scale: 1.0,
  centreOnPlate: true,
  needsSupport: false,
  quality: 'standard',
};

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

/**
 * Build a .3mf Blob from a raw binary STL Blob (as produced by OpenSCAD WASM).
 * The STL is parsed into a BufferGeometry, scaled/positioned, then written
 * out as 3MF XML embedded in a ZIP archive.
 */
export async function stlBlobTo3mf(
  stlBlob: Blob,
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): Promise<Blob> {
  const buffer = await stlBlob.arrayBuffer();
  const loader = new STLLoader();
  const geometry = loader.parse(buffer);
  return geometryTo3mf(geometry, settings);
}

/**
 * Build a .3mf Blob from an already-loaded Three.js BufferGeometry.
 */
export function geometryTo3mf(
  geometry: THREE.BufferGeometry,
  settings: PrintSettings = DEFAULT_PRINT_SETTINGS,
): Blob {
  // 1. Clone so we don't mutate the live display geometry
  const geom = geometry.clone();

  // 2. Convert to non-indexed if needed (STLLoader already produces non-indexed)
  if (geom.index) {
    const nonIndexed = geom.toNonIndexed();
    geom.dispose();
    return geometryTo3mf(nonIndexed, settings);
  }

  // 3. Apply scale — Three.js positions are in metres for AI meshes but OpenSCAD
  //    WASM already outputs in mm (confirmed by meshPrintProcessUtils constants).
  //    The caller is responsible for the scale factor in settings.
  if (settings.scale !== 1.0) {
    geom.scale(settings.scale, settings.scale, settings.scale);
  }

  // 4. Centre on build plate (XZ plane centred, bottom flush with Z=0)
  if (settings.centreOnPlate) {
    geom.computeBoundingBox();
    const box = geom.boundingBox!;
    const cx = (box.min.x + box.max.x) / 2;
    const cy = (box.min.y + box.max.y) / 2;
    // Move so bottom sits at Z = 0. OpenSCAD uses Z-up so Y in three = Z in scad.
    const cz = box.min.y;
    geom.translate(-cx, -cz, -cy);
  }

  // 5. Build XML strings
  const modelXml = buildModelXml(geom, settings);
  const contentTypesXml = buildContentTypesXml();
  const relsXml = buildRelsXml();

  // 6. Pack into ZIP
  const files: ZipEntry[] = [
    { name: '[Content_Types].xml', data: encodeUtf8(contentTypesXml) },
    { name: '_rels/.rels', data: encodeUtf8(relsXml) },
    { name: '3D/3dmodel.model', data: encodeUtf8(modelXml) },
  ];

  geom.dispose();

  return new Blob([buildZip(files)], {
    type: 'application/vnd.ms-package.3dmanufacturing-3dmodel+xml',
  });
}

// ---------------------------------------------------------------------------
// 3MF XML builders
// ---------------------------------------------------------------------------

function buildContentTypesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/>
</Types>`;
}

function buildRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>
</Relationships>`;
}

function buildModelXml(
  geom: THREE.BufferGeometry,
  settings: PrintSettings,
): string {
  const positions = geom.attributes.position;
  if (!positions) throw new Error('Geometry has no position attribute');

  const vertexCount = positions.count;
  const triangleCount = vertexCount / 3;

  // Build vertex list
  const vertexLines: string[] = [];
  for (let i = 0; i < vertexCount; i++) {
    const x = positions.getX(i).toFixed(6);
    const y = positions.getY(i).toFixed(6);
    const z = positions.getZ(i).toFixed(6);
    vertexLines.push(`      <vertex x="${x}" y="${y}" z="${z}"/>`);
  }

  // Build triangle list (each 3 consecutive vertices form a triangle)
  const triangleLines: string[] = [];
  for (let t = 0; t < triangleCount; t++) {
    const v1 = t * 3;
    const v2 = t * 3 + 1;
    const v3 = t * 3 + 2;
    triangleLines.push(`      <triangle v1="${v1}" v2="${v2}" v3="${v3}"/>`);
  }

  const qualityLabel = {
    draft: 'Draft (0.3mm layer)',
    standard: 'Standard (0.2mm layer)',
    fine: 'Fine (0.1mm layer)',
  }[settings.quality];

  // Escape XML special chars in model name
  const safeName = settings.modelName
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  return `<?xml version="1.0" encoding="UTF-8"?>
<model unit="${settings.unit}" xml:lang="en-US"
  xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02"
  xmlns:p="http://schemas.microsoft.com/3dmanufacturing/production/2015/06">
  <metadata name="Application">Gienox 3D</metadata>
  <metadata name="Title">${safeName}</metadata>
  <metadata name="Description">Generated by Gienox AI CAD — 3d.gienox.com</metadata>
  <metadata name="PrintQuality">${qualityLabel}</metadata>
  <metadata name="SupportNeeded">${settings.needsSupport ? 'yes' : 'no'}</metadata>
  <resources>
    <object id="1" type="model" name="${safeName}">
      <mesh>
        <vertices>
${vertexLines.join('\n')}
        </vertices>
        <triangles>
${triangleLines.join('\n')}
        </triangles>
      </mesh>
    </object>
  </resources>
  <build>
    <item objectid="1"/>
  </build>
</model>`;
}

// ---------------------------------------------------------------------------
// Minimal ZIP writer (no external deps)
// Implements ZIP local file headers + central directory per APPNOTE.TXT
// ---------------------------------------------------------------------------

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

function buildZip(entries: ZipEntry[]): ArrayBuffer {
  const localHeaders: Uint8Array[] = [];
  const centralDirEntries: Uint8Array[] = [];
  const offsets: number[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encodeUtf8(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.byteLength;

    // Local file header (signature 0x04034b50)
    const localHeader = new Uint8Array(30 + nameBytes.byteLength + size);
    const lv = new DataView(localHeader.buffer);
    lv.setUint32(0, 0x04034b50, true); // signature
    lv.setUint16(4, 20, true);          // version needed (2.0)
    lv.setUint16(6, 0, true);           // general purpose bit flag
    lv.setUint16(8, 0, true);           // compression: stored
    lv.setUint16(10, 0, true);          // last mod time
    lv.setUint16(12, 0, true);          // last mod date
    lv.setUint32(14, crc, true);        // crc-32
    lv.setUint32(18, size, true);       // compressed size
    lv.setUint32(22, size, true);       // uncompressed size
    lv.setUint16(26, nameBytes.byteLength, true); // file name length
    lv.setUint16(28, 0, true);          // extra field length
    localHeader.set(nameBytes, 30);
    localHeader.set(entry.data, 30 + nameBytes.byteLength);

    offsets.push(offset);
    offset += localHeader.byteLength;
    localHeaders.push(localHeader);

    // Central directory entry (signature 0x02014b50)
    const cdEntry = new Uint8Array(46 + nameBytes.byteLength);
    const cv = new DataView(cdEntry.buffer);
    cv.setUint32(0, 0x02014b50, true);  // signature
    cv.setUint16(4, 20, true);           // version made by
    cv.setUint16(6, 20, true);           // version needed
    cv.setUint16(8, 0, true);            // bit flag
    cv.setUint16(10, 0, true);           // compression
    cv.setUint16(12, 0, true);           // last mod time
    cv.setUint16(14, 0, true);           // last mod date
    cv.setUint32(16, crc, true);         // crc-32
    cv.setUint32(20, size, true);        // compressed size
    cv.setUint32(24, size, true);        // uncompressed size
    cv.setUint16(28, nameBytes.byteLength, true); // file name length
    cv.setUint16(30, 0, true);           // extra field length
    cv.setUint16(32, 0, true);           // file comment length
    cv.setUint16(34, 0, true);           // disk number start
    cv.setUint16(36, 0, true);           // internal attributes
    cv.setUint32(38, 0, true);           // external attributes
    cv.setUint32(42, offsets[offsets.length - 1], true); // relative offset
    cdEntry.set(nameBytes, 46);
    centralDirEntries.push(cdEntry);
  }

  const cdOffset = offset;
  const cdSize = centralDirEntries.reduce((s, e) => s + e.byteLength, 0);

  // End of central directory record (signature 0x06054b50)
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true);          // signature
  ev.setUint16(4, 0, true);                    // disk number
  ev.setUint16(6, 0, true);                    // disk with CD start
  ev.setUint16(8, entries.length, true);       // entries on disk
  ev.setUint16(10, entries.length, true);      // total entries
  ev.setUint32(12, cdSize, true);              // size of central directory
  ev.setUint32(16, cdOffset, true);            // offset of central directory
  ev.setUint16(20, 0, true);                   // comment length

  // Concatenate all parts
  const totalSize =
    localHeaders.reduce((s, h) => s + h.byteLength, 0) +
    centralDirEntries.reduce((s, e) => s + e.byteLength, 0) +
    eocd.byteLength;

  const out = new Uint8Array(totalSize);
  let pos = 0;
  for (const h of localHeaders) { out.set(h, pos); pos += h.byteLength; }
  for (const c of centralDirEntries) { out.set(c, pos); pos += c.byteLength; }
  out.set(eocd, pos);

  return out.buffer;
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

function encodeUtf8(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

/** CRC-32 implementation (standard polynomial 0xEDB88320) */
function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.byteLength; i++) {
    crc ^= data[i];
    for (let j = 0; j < 8; j++) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}
